import moment from 'moment/moment';
import app from 'config/app.json';
import { buildPublicationCertificationUrl, cleanUrl, getServerBaseUrl } from 'utils/urlUtils';
import FilesList from 'components/Lists/FilesList';
import { formatDate } from 'utils/formatTime';
import { AccessorType } from 'hook/useConf';
import { buildArcadeFileContentUrl } from './urlUtils';

/**
 * Gets the content rendition URL.
 * @param contentId the content ID to display
 * @returns {string}
 */
export function getContentThumbnailUrl(contentId) {
  return cleanUrl(
    `${cleanUrl(`${getServerBaseUrl()}/arcade/api/entities`)}/${contentId}/thumbnail`
  );
}

/**
 * Find child entities of a root entity.
 *
 * @param entity the root entities
 * @param entityTypeCode the child entity type code to search
 * @param parent the parent entity
 * @returns {*[]}
 */
export function findByEntityType(entity, entityTypeCode, parent) {
  const entities = entity instanceof Array ? entity : [entity];

  const res = [];
  entities.forEach((entity) => {
    if (entity.entityTypeCode === entityTypeCode) {
      res.push({
        ...entity,
        parent
      });
    }

    if (entity.children) {
      entity.children.forEach((child) => {
        res.push(...findByEntityType(child, entityTypeCode, entity));
      });
    }
  });

  return res;
}

/**
 * Gets an entity parameter.
 *
 * @param entity the entity
 * @param propertyTypeCode the property type code
 * @returns {null|unknown}
 */
export function getEntityParameter(entity, propertyTypeCode) {
  if (!entity) return null;
  const firstPart = propertyTypeCode?.substring(0, propertyTypeCode?.indexOf('.'));
  if (firstPart) {
    const subEntity = entity[firstPart];
    if (subEntity) {
      return getEntityParameter(
        subEntity,
        propertyTypeCode.substring(propertyTypeCode.indexOf('.') + 1)
      );
    }
  }
  return getParameter(entity.parameters, propertyTypeCode);
}

/**
 * Gets an parameter.
 *
 * @param parameters the parameters
 * @param propertyTypeCode the property type code
 * @returns {null|unknown}
 */
export function getParameter(parameters, propertyTypeCode) {
  return parameters?.find((x) => x.propertyTypeCode === propertyTypeCode);
}

const FILTER_VALUE_SEPARATOR = '|';

const FILTER_OPERATORS = Object.freeze({
  STARTS_WITH_CS: (value, token) => value.startsWith(token),
  STARTS_WITH_CI: (value, token) => value.toUpperCase().startsWith(token.toUpperCase()),
  EQUALS_CS: (value, token) => value === token,
  EQUALS_CI: (value, token) => value.toUpperCase() === token.toUpperCase()
});

const DEFAULT_FILTER_OPERATOR = 'STARTS_WITH_CS';

/**
 * Gets the comparison function matching a filter operator code. An unknown code falls back on the
 * default operator.
 *
 * @param filterOperator the filter operator code
 * @returns {function(string, string): boolean}
 */
function getFilterComparator(filterOperator) {
  if (!filterOperator) {
    return FILTER_OPERATORS[DEFAULT_FILTER_OPERATOR];
  }
  const comparator = FILTER_OPERATORS[filterOperator];
  if (!comparator) {
    console.warn(
      `Invalid filterOperator '${filterOperator}'. Default filter operator ${DEFAULT_FILTER_OPERATOR} is used.`
    );
    return FILTER_OPERATORS[DEFAULT_FILTER_OPERATOR];
  }
  return comparator;
}

/**
 * Tests an entity against a filter definition (filterPropertyCode, filterOperator, filterValue),
 * with the same semantics as the entity files filtering. An entity matches when no filter property
 * or no filter value is defined.
 *
 * @param entity the entity to test
 * @param filterConf the filter definition
 * @returns {boolean} true when the entity satisfies the filter
 */
function matchesEntityFilter(entity, filterConf) {
  const { filterPropertyCode, filterOperator, filterValue } = filterConf ?? {};
  if (!filterPropertyCode || !filterValue) {
    return true;
  }
  const value = getParameter(entity?.parameters, filterPropertyCode)?.value;
  if (value == null) {
    return false;
  }
  const comparator = getFilterComparator(filterOperator);
  return filterValue
    .split(FILTER_VALUE_SEPARATOR)
    .filter((token) => token !== '')
    .some((token) => comparator(value, token));
}

/**
 * Gets the grouping value carried by a set of sibling entities.
 *
 * The value is read on the first entity of the configured entity type. When the optional
 * discriminant (filterPropertyCode, filterOperator, filterValue) is defined, only the entities
 * satisfying it are candidates; without it the retained entity is undetermined as soon as several
 * entities of the same type live under the same parent.
 *
 * @param entities the sibling entities
 * @param groupConf the group configuration
 * @returns {string|undefined} the grouping value, or undefined when no entity carries it
 */
export function getGroupValue(entities, groupConf) {
  const groupEntity = entities?.find(
    (entity) =>
      entity.entityTypeCode === groupConf?.documentEntityTypeCode &&
      matchesEntityFilter(entity, groupConf)
  );
  return getParameter(groupEntity?.parameters, groupConf?.documentPropertyTypeCode)?.value;
}

/**
 * Gets an entity parameter value.
 *
 * @param entityParameter the entity parameter
 * @param format the value formatter
 * @returns {null|unknown}
 */
export function extractEntityParameterValue(entityParameter, format) {
  if (entityParameter?.propertyTypeType === 'DATETIME') {
    if (format === 'NONE') {
      return moment(entityParameter.objectValue);
    }
    return formatDate(
      entityParameter.objectValue,
      format != null ? format : app.format.dateTime.display
    );
  }
  if (entityParameter?.propertyTypeType === 'BOOLEAN') {
    return Boolean(entityParameter.objectValue);
  }
  if (entityParameter?.propertyTypeType === 'INTEGER') {
    return parseInt(entityParameter.objectValue, 10);
  }
  if (entityParameter?.propertyTypeType === 'DECIMAL') {
    return parseFloat(entityParameter.objectValue);
  }
  return entityParameter?.objectValue ?? '';
}

export function formatValue(value, format) {
  if (value == null || value === '') return '';
  return format ? moment(value).format(format) : value;
}

/**
 * Gets an parameter value.
 *
 * @param parameters the parameters
 * @param propertyTypeCode the property type code
 * @param format the value formatter
 * @returns {null|unknown}
 */
export function getParameterValue(parameters, propertyTypeCode, format) {
  const parameter = getParameter(parameters, propertyTypeCode);
  if (!parameter) {
    return '';
  }
  return extractEntityParameterValue(parameter, format);
}

/**
 * Gets an entity parameter value.
 *
 * @param entity the entity
 * @param propertyTypeCode the property type code
 * @param format the value formatter
 * @returns {null|unknown}
 */
export function getEntityPropertyValue(entity, propertyTypeCode, format) {
  const entityParameter = getEntityParameter(entity, propertyTypeCode);
  if (entityParameter) {
    return extractEntityParameterValue(entityParameter, format);
  }

  // Gets nested value
  const value = propertyTypeCode?.split('.').reduce((acc, key) => {
    if (!acc) return undefined;

    // Special case to retrieve a child : children[TYPE]
    const match = key.match(/^children\[(.+)]$/);
    if (match) {
      const entityTypeCode = match[1];
      return acc.children?.find((c) => c.entityTypeCode === entityTypeCode);
    }

    return acc?.[key];
  }, entity);

  // Process native property
  if (propertyTypeCode?.includes('Date')) {
    return formatDate(value, format != null ? format : app.format.dateTime.display);
  }
  return value || '';
}

/**
 * Builds a structured Object from entity parameters. For example : {SEANCE_ASSEMBLY_LABEL: 'Commission Permanente, SEANCE_START_DATE: '4 septembre 2022'}
 * @param entities one or several entities
 * @param dateFormat the date format to apply on parameters of type datetime
 */
export function getEntityParametersAsObject(entities, dateFormat) {
  const results = [];
  (entities instanceof Array ? entities : [entities]).forEach((entity) => {
    const items = entity?.parameters?.map((param) => {
      let format;
      if (param.propertyTypeType === 'DATETIME') {
        format = dateFormat;
      }
      return [
        `${param.propertyTypeCode}`,
        getEntityPropertyValue(entity, param.propertyTypeCode, format)
      ];
    });
    if (items) {
      results.push(items);
    }
  });
  return Object.fromEntries(results.flat());
}

const numericCollator = new Intl.Collator([], { numeric: true });
const alphaCollator = new Intl.Collator([]);

/**
 * Sort entities base on the value of specified property type code.
 * @param entities the entities to sort
 * @param conf the conf. Configuration definition example:
 * {
 *    "propertyTypeCode": "DELIB_NUMBER",
 *    "type": "numeric",
 *    "direction": "desc"
 * }
 * @returns {(function(*, *): (number))|*}
 */
export function sortEntities(entities, conf) {
  entities.sort((elem1, elem2) => {
    if (!Array.isArray(conf)) {
      const arr = [];
      arr.push(conf);
      conf = arr;
    }

    const res = [];
    conf?.forEach((elem) => {
      const val1 = getEntityPropertyValue(elem1, elem?.propertyTypeCode, 'YYYYMMDD');
      const val2 = getEntityPropertyValue(elem2, elem?.propertyTypeCode, 'YYYYMMDD');

      let compare;
      if (elem?.type === 'numeric') {
        compare = numericCollator.compare(val1, val2);
      } else {
        compare = alphaCollator.compare(val1, val2);
      }
      if (elem?.direction === 'desc') {
        compare *= -1;
      }
      res.push(compare);
    });
    return res.reduce((previousValue, currentValue) => previousValue || currentValue);
  });
  return entities;
}

/**
 * Gets the entity root.
 *
 * @param entity the entity
 * @returns {null|unknown}
 */
export function getRoot(entity) {
  if (!entity) {
    return null;
  }
  let res = entity;
  while (res.parent) {
    res = res.parent;
  }

  return res;
}

/**
 * Gets a valid date.
 * @param date an entity date
 * @returns {*|string}
 */
export function getValidDate(date) {
  if (date?.endsWith('24:00:00')) {
    return date.substring(0, date.lastIndexOf(' '));
  }
  return date;
}

/**
 * Returns an item replacing "accessor" property using table columns definition.
 * @param entity the entity
 * @param tableColumns table columns definition
 * @param viewName the view
 * @returns {*}
 */
export function mapEntityProperties(entity, tableColumns, viewName) {
  const mappedItem = {
    ...entity.entity
  };
  tableColumns?.forEach((column) => {
    const { accessor, accessorType, format } = column;
    mappedItem[accessor] = getColumnValue(entity, accessor, accessorType, format, viewName);
  });
  return mappedItem;
}

/**
 * Gets the item property value using property code.
 * @param item the item (entity, row,...)
 * @param propertyCode the property code. Multiple property codes are separeted by coma
 * @returns {*|string}
 */
export function getPropertyValue(item, propertyCode) {
  if (!propertyCode) {
    return '';
  }
  return (
    propertyCode
      .split(',')
      .map((key) => item[key])
      .find((value) => value) || ''
  );
}

/**
 * Retrieves a value from an item based on the specified accessor type.
 *
 * @param {Object} item - The source item from which values are to be retrieved.
 * @param {string} accessor - The key or method through which the value is accessed from the item.
 * @param {string} accessorType - The type of accessor which determines how the value is fetched. It should be one of the predefined AccessorType enum values.
 * @param {string} format - The formatting rule to apply to the retrieved value, if applicable.
 * @param {string} viewName - An optional view name that might be used in the rendering context, specifically when dealing with files.
 * @returns {*} The retrieved and optionally formatted value, or the result from `optimisticColumnValue` as a fallback.
 */
export function getColumnValue(item, accessor, accessorType, format, viewName) {
  switch (accessorType) {
    case AccessorType.Action:
      return item;
    case AccessorType.File:
      return (
        <FilesList files={item[accessor]} selectedId={item.entity.contentId} viewName={viewName} />
      );
    case AccessorType.EntityProperty:
      return formatValue(item.entity[accessor], format);
    case AccessorType.ExtraParameter:
      return getParameterValue(item.extraParameters, accessor, format);
    case AccessorType.Parameter:
      return getEntityPropertyValue(item.entity, accessor, format);
    case AccessorType.Property:
      return item[accessor];
    default:
      return optimisticColumnValue(item, accessor, format);
  }
}

/**
 * Obtains the first non-empty, non-null, and non-undefined property value based on a given accessor and format.
 * It iterates over a list of potential value sources, applying the accessor and format, and returns the first valid value.
 *
 * @param {Object} item - The item from which to extract the value.
 * @param {string} accessor - The property name to be used for value extraction.
 * @param {string} format - The format to be applied to the property value.
 * @returns {*} The first non-empty, non-null, and non-undefined value found, or undefined if none.
 */
export function optimisticColumnValue(item, accessor, format) {
  const valueSources = [
    item.entity ? formatValue(item.entity[accessor], format) : undefined,
    item.extraParameters ? getParameterValue(item.extraParameters, accessor, format) : undefined,
    item.entity ? getEntityPropertyValue(item.entity, accessor, format) : undefined,
    getEntityPropertyValue(item, accessor, format),
    item[accessor]
  ];

  return valueSources.find((value) => value !== undefined && value !== null && value !== '') || '';
}

/**
 * Download content.
 * @param contentId the file contentId to download
 * @param filename the file name to download
 */
export function downloadContent(contentId, filename) {
  const a = document.createElement('a');
  a.href = buildArcadeFileContentUrl(contentId);
  a.download = filename ?? contentId ?? 'unknown';
  a.click();
}

export function downloadPublicationCertificate(documentId) {
  const a = document.createElement('a');
  a.href = buildPublicationCertificationUrl(documentId);
  a.click();
}

/**
 * Open content in new browser tab.
 * @param contentId the file content ID to open
 */
export function openContent(contentId) {
  const hiddenElement = document.createElement('a');
  hiddenElement.href = buildArcadeFileContentUrl(contentId);
  hiddenElement.target = '_blank';
  hiddenElement.click();
}

export function getDeepNestedFieldValue(path, obj) {
  if (!obj) {
    return null;
  }
  return path.split('.').reduce((p, c) => {
    if (p && p[c] !== undefined) {
      return p[c];
    }
    return null;
  }, obj);
}

/**
 * Retrieves the value associated with a given code from the stored search form in the session storage.
 *
 * @param {string} code - The key corresponding to the value to be retrieved from the search form.
 * @returns {string} The value associated with the given code, or an empty string if not found.
 */
export function getSearchFormValue(code) {
  const searchFormJson = sessionStorage.getItem('searchForm');
  if (searchFormJson == null) return '';
  const searchForm = JSON.parse(searchFormJson);
  if (searchForm == null) return '';
  return searchForm[code] ?? '';
}

/**
 * Gets file viewer title based on entity.
 * @param entity the entity
 * @param defaultTitle the default title used if no title can b retrieved from entity
 * @param msg the message bundle retriever
 * @returns {*}
 */
export function getFileViewerTitle(entity, defaultTitle, msg) {
  if (!entity) {
    return defaultTitle;
  }
  const getEntityWithParents = (entity) =>
    entity ? [entity, ...getEntityWithParents(entity.parent)] : [];
  try {
    return msg(
      {
        id: `view.fileViewer.${entity.entityTypeCode}.title`,
        defaultMessage: defaultTitle
      },
      getEntityParametersAsObject(getEntityWithParents(entity), app.format.date.display)
    );
  } catch (error) {
    console.warn(error);
    return entity.name ?? defaultTitle;
  }
}
